/**
 * Simple "glass pane" stats overlay - an Unreal-`stat unit`-style frame
 * time breakdown, camera position, and (where the browser actually
 * exposes it) memory usage. Toggled by the hamburger-style button on the
 * far right of FtgsPlaybackBar.
 *
 * The rows mirror Unreal's `stat unit`, arranged as a three-level tree:
 *
 *     FRAME                      whole rAF period (present to present)
 *       CPU                        UPDATE + SUBMIT
 *         UPDATE                     update phase (Unreal's Game - renamed,
 *                                    this is a viewer, not a game)
 *         SUBMIT                     CPU cost of building and submitting
 *                                    the frame (Unreal's Draw)
 *       GPU                        SORT + DRAW, from WebGPU timestamp queries
 *         SORT                       depth sort (a compute pass)
 *         DRAW                       the render passes
 *
 * CPU and GPU are SIBLINGS, and FRAME is the LARGER of them - not their sum.
 * The CPU prepares frame N+1 while the GPU is still drawing frame N, so the
 * two overlap and the frame interval is set by whichever is slower (plus a
 * vsync floor). That is why the panel puts them side by side: the row
 * sitting closest to FRAME is the current bottleneck, and optimising the
 * other one moves nothing. Each half's two children do sum to their parent.
 *
 * See main.ts's frame-breakdown hooks for exactly where each one is
 * measured - by the time they arrive here they are already smoothed.
 * (Unreal's fifth line, RHIT, has no counterpart: the browser runs update
 * and render sequentially on one thread, so there is no separate RHI thread
 * to attribute.)
 *
 * GPU is itself split into SORT and DRAW, one level deeper. Unlike a game,
 * this renderer spends a large and variable share of its GPU frame on one
 * non-drawing step - depth-sorting every splat so alpha blending composites
 * in the right order - and that share is the single most useful number when
 * comparing machines or builds. The two sub-rows sum to GPU by
 * construction; see main.ts's readFrameTimings for how the sort's per-sort
 * cost is scaled onto frames.
 *
 * Why the ms rows matter more than the FPS number: FRAME is vsync-capped
 * and the browser gives no way to turn that off, so on a 165Hz display a
 * scene doing 3ms of real work and one doing 5ms both report the same
 * FPS. Only UPDATE/SUBMIT/GPU show how much of the frame budget is actually
 * left - which is the entire reason they are here.
 *
 * No CPU/GPU/VRAM usage rows: standard web pages have NO API to read
 * per-process/private CPU usage, GPU usage, or VRAM usage at all - browsers
 * deliberately don't expose this (it's a real fingerprinting/privacy
 * surface, not an oversight). An earlier version showed these as permanent
 * "N/A" rows plus an explanatory note; both were removed per user request
 * to keep the panel to only what's actually real - see git history if
 * that's wanted back. RAM is shown as the JS heap size via the non-standard
 * `performance.memory` (Chrome/Edge only; still not "system RAM", just this
 * tab's JS heap) - falls back to N/A on browsers that don't have it
 * (Firefox, Safari).
 *
 * The readouts (Frame through Splats) sit in a collapsible block (user
 * request 2026-10-21): a "Performance" header row shows just the FPS while
 * collapsed (the default) and expands to the full list on click. The
 * open/closed state is remembered per browser (localStorage).
 */

import { Container } from '@playcanvas/pcui';

import { FtgsCollapsible } from './ftgs-collapsible';

type PerformanceMemory = { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number };

/**
 * One frame's `stat unit` breakdown, all in milliseconds and all already
 * smoothed by the caller (frameMs/fps as a true 1s-window average so
 * hitches count at full weight, the rest as EMAs - see main.ts's
 * frame-breakdown hooks). Two sentinel values, meaning genuinely different
 * things (see formatMs): 0 is "no sample" - either nothing measured yet,
 * or, for `submitMs`, a tick that didn't draw at all, since SuperSplat
 * renders on demand (Scene.onUpdate's renderNextFrame logic). -1 is
 * "unmeasurable on this machine", currently only `gpuMs` on a device
 * without timestamp-query support.
 */
type FrameTimings = {
    fps: number;
    frameMs: number;
    /** UPDATE + SUBMIT - the frame's whole CPU cost (see the two below) */
    cpuMs: number;
    updateMs: number;
    submitMs: number;
    /** SORT + DRAW - the frame's whole GPU cost (see the two below) */
    gpuMs: number;
    /**
     * Depth-sort share of the frame's GPU time. The sorter measures the cost
     * of ONE sort (its own timestamp wrap - the sort submits outside the
     * engine's render passes, so the profiler behind drawMs cannot see it),
     * and main.ts scales that onto frames by the measured sort:frame ratio:
     * 1.0 every-frame (Sort Limit off), ~20/fps once throttled. Already
     * scaled by the time it arrives here.
     */
    sortMs: number;
    /** Render-pass share of the frame's GPU time - clear, splat draw, blit */
    drawMs: number;
};

const getMemoryInfo = (): PerformanceMemory | null => {
    const perf = performance as unknown as { memory?: PerformanceMemory };
    return perf.memory ?? null;
};

const NOT_AVAILABLE = 'N/A';

/** localStorage key remembering whether the Performance block is expanded */
const METRICS_OPEN_KEY = 'ftgs-stats-metrics-open';

// nothing measured yet, or nothing to measure this frame - see FrameTimings
const NO_SAMPLE = '-';

/**
 * Two decimals, not one: the sub-rows quantize hard at 0.1ms - UPDATE and
 * SUBMIT sit around 0.4-0.6ms, where a single decimal turns a real 20%
 * change into one flickering digit - and SORT + DRAW must be seen to add up
 * to the GPU row above them, which rounding to 0.1 visibly breaks.
 */
const MS_DECIMALS = 2;

/**
 * Render one timing row's value, keeping the three states distinct: -1 is
 * "this machine can never measure it" (N/A), 0 is "no sample yet, or this
 * frame had none" (-), anything else is a real number. Without the middle
 * case the GPU and SUBMIT rows read "0.00 ms" for the first second after the
 * panel opens, which looks like a measurement rather than the absence of
 * one.
 */
const formatMs = (ms: number): string => {
    if (ms < 0) {
        return NOT_AVAILABLE;
    }
    return ms === 0 ? NO_SAMPLE : `${ms.toFixed(MS_DECIMALS)} ms`;
};

class FtgsStatsOverlay extends Container {
    private frameValue: HTMLElement;
    private updateValue: HTMLElement;
    private submitValue: HTMLElement;
    private cpuValue: HTMLElement;
    private gpuValue: HTMLElement;
    private sortValue: HTMLElement;
    private drawValue: HTMLElement;
    private ramValue: HTMLElement;
    private cameraValue: HTMLElement;
    private targetValue: HTMLElement;
    private resolutionValue: HTMLElement;
    private splatsValue: HTMLElement;
    private variantValue: HTMLElement;
    private metrics: FtgsCollapsible;

    /** caller-supplied handler - left null until main.ts wires it up */
    onClearCache: (() => void) | null = null;

    // timing text refreshes at most once/sec (see update()'s doc) -
    // everything else (camera, RAM, resolution) still updates every call
    private lastTimingRenderTime = 0;
    private readonly TIMING_RENDER_INTERVAL_MS = 1000;

    constructor(args: Record<string, unknown> = {}) {
        super({ ...args, id: 'ftgs-stats-overlay', hidden: true });
        // the panel scrolls past the window height (ftgs-stats-overlay.scss),
        // which clips its hover bubbles - ftgs-tooltip-fit.ts keeps them
        // inside it
        this.dom.classList.add('ftgs-tip-clip');

        // sits over the canvas - don't let interactions here reach the
        // camera controller underneath (same pattern as the other overlays)
        ['pointerdown', 'pointerup', 'pointermove', 'wheel', 'dblclick'].forEach((eventName) => {
            this.dom.addEventListener(eventName, (event: Event) => event.stopPropagation());
        });

        // where createRow puts its row - the panel itself, or the
        // collapsible Performance block while its rows are being built
        let rowParent: HTMLElement = this.dom;

        // `sub2` is one indent deeper than `sub`: a breakdown of a row that
        // is itself a breakdown (GPU's Sort/Draw split under FRAME).
        const createRow = (label: string, initialValue: string, sub = false, tooltip?: string, path = false, sub2 = false): HTMLElement => {
            const row = document.createElement('div');
            row.className = sub || sub2 ? 'ftgs-stats-row ftgs-stats-row-sub' : 'ftgs-stats-row';
            if (sub2) {
                row.classList.add('ftgs-stats-row-sub2');
            }
            if (path) {
                // a URL is far too long for the label-left/value-right
                // single line the other rows use - stack it instead, and
                // let it break anywhere so the panel keeps its width
                row.classList.add('ftgs-stats-row-path');
            }

            // An empty label means the value speaks for itself (the path
            // row): no caption is rendered at all, and the hover bubble hangs
            // off the value instead of off a label that isn't there.
            const labelEl = label ? document.createElement('span') : null;
            if (labelEl) {
                labelEl.className = 'ftgs-stats-label';
                labelEl.textContent = label;
            }

            const valueEl = document.createElement('span');
            valueEl.className = 'ftgs-stats-value';
            valueEl.textContent = initialValue;

            if (tooltip) {
                // black hover bubble (same data-tooltip + ::after mechanism
                // as the stereo controls' - see ftgs-stats-overlay.scss)
                (labelEl ?? valueEl).dataset.tooltip = tooltip;
            }

            if (labelEl) {
                row.appendChild(labelEl);
            }
            row.appendChild(valueEl);
            rowParent.appendChild(row);
            return valueEl;
        };

        // TOP ROW (user request 2026-09-09): the file this session is
        // actually playing - which dataset, which rate-ladder folder,
        // which filename. It answers "what am I looking at" before the
        // controls that change it and the numbers that measure it, and it
        // is the only line that stays meaningful when Splats cannot move
        // (every rate point encodes the same 499,840 gaussians).
        //
        // Deliberately CAPTION-LESS, unlike every other row: a path says what
        // it is on its own, where "4.8 ms" or "499,840" would not. It also
        // sits in the panel's title position, so it reads as the heading it
        // effectively is.
        //
        // This row used to show the splat RECORD LAYOUT ("gsc-vsei" /
        // "legacy"), an A-migration transition indicator. That migration
        // finished on 2026-09-07 and the legacy layout was deleted, leaving
        // the row a constant - so it was repurposed rather than kept as
        // decoration. splat-layout.ts is still the single definition of the
        // layout itself; nothing about it is per-dataset any more.
        this.variantValue = createRow('', '-', false, [
            'File the loaded segments are actually coming from',
            'relative to uploads/ - dataset, ladder folder, filename',
            'BIN names the bitstream, PLY the decoded segment'
        ].join('\n'), true);

        // Performance header - toggles the readout block below it. Shows
        // the FPS on its right so the one number people glance at most is
        // still there while the block is collapsed.
        this.metrics = new FtgsCollapsible('Performance', METRICS_OPEN_KEY);
        this.metrics.summary.textContent = NO_SAMPLE;
        this.metrics.appendTo(this.dom);
        rowParent = this.metrics.body;

        // `stat unit` block - FRAME is the total, the three indented rows
        // below are what it went on
        this.frameValue = createRow('Frame', '-', false, [
            'Total time of one frame and the FPS derived from it',
            'one requestAnimationFrame period'
        ].join('\n'));
        // FRAME's two halves, each with its own breakdown one level deeper.
        // Keeping CPU and GPU as siblings is the whole point of this shape:
        // FRAME is the LARGER of the two, not their sum, because the CPU
        // prepares frame N+1 while the GPU is still drawing frame N.
        // Whichever of these two rows sits closest to FRAME is the current
        // bottleneck - which is the one thing this panel exists to show.
        this.cpuValue = createRow('CPU', '-', true, [
            'CPU time for one frame',
            'the Update and Submit rows below add up to this',
            'FRAME is the larger of CPU and GPU, not their sum -',
            'the two overlap across frames'
        ].join('\n'));
        this.updateValue = createRow('Update', '-', true, [
            'CPU time of the update phase',
            'playback, animation and scene logic'
        ].join('\n'), false, true);
        this.submitValue = createRow('Submit', '-', true, [
            'CPU time to build the draw commands',
            'and submit them to the GPU'
        ].join('\n'), false, true);
        // The last two lines pre-empt the obvious question when this row
        // reads above FRAME (seen: GPU 5.34 vs FRAME 4.95): Sort and Draw
        // are timestamp SPANS, and the GPU starts setting up the next pass
        // while the previous one's last waves drain, so the two spans
        // overlap by a few hundred us at their boundary and that sliver is
        // counted in both. Real GPU occupancy cannot exceed the frame; the
        // arithmetic sum of two spans can, by roughly that overlap.
        this.gpuValue = createRow('GPU', '-', true, [
            'Time the GPU spent on one frame',
            'the Sort and Draw rows below add up to this',
            'Sort and Draw overlap slightly at their boundary,',
            'so their sum can read a little above FRAME'
        ].join('\n'));

        // GPU's own breakdown, one level deeper than FRAME's. Splat
        // rendering spends a large share of the GPU frame on depth sorting
        // rather than drawing, and keeping that visible is the point of the
        // split. The two sub-rows sum to GPU by construction - see main.ts's
        // readFrameTimings for how the sort's per-sort cost is scaled onto
        // frames before it gets here.
        this.sortValue = createRow('Sort', '-', true, [
            'GPU time spent depth-sorting the splats',
            'so alpha blending composites in the right order',
            'averaged over frames when Sort Limit throttles it'
        ].join('\n'), false, true);
        this.drawValue = createRow('Draw', '-', true, [
            'GPU time spent in the render passes',
            'clear, the splat draw, and the final blit'
        ].join('\n'), false, true);

        this.ramValue = createRow('RAM (JS Heap)', getMemoryInfo() ? '-' : NOT_AVAILABLE, false, [
            'JavaScript heap memory currently in use',
            'GPU buffers/textures are not included',
            'Chromium-only (performance.memory)'
        ].join('\n'));
        this.cameraValue = createRow('Camera Position', '-', false, [
            'Camera position (x, y, z)',
            'world-space scene units, updated live',
            'dataset.json cameraSpawn takes these numbers'
        ].join('\n'));
        // the look-at point (2026-10-21): a right-drag pan moves it along with
        // the camera, so the position alone can't bring a view back -
        // dataset.json's cameraTarget takes these numbers next to cameraSpawn
        this.targetValue = createRow('Camera Target', '-', false, [
            'Point the camera looks at and orbits around (x, y, z)',
            'world-space scene units, updated live',
            'dataset.json cameraTarget takes these numbers'
        ].join('\n'));
        // actual GPU-rendered pixel size (canvas.width/height, i.e. CSS size
        // * devicePixelRatio - see scene.ts's ResizeObserver), NOT the CSS
        // display size - this is what answers "is it really rendering at
        // 4K" since it's the true per-frame render target resolution.
        this.resolutionValue = createRow('Resolution', '-', false, [
            'Actual GPU-rendered pixel size',
            'CSS canvas size × devicePixelRatio',
            'the true per-frame render target resolution'
        ].join('\n'));
        // the currently-loaded segment's real (non-padding) splat count -
        // changes per segment swap during playback, not just per dataset
        this.splatsValue = createRow('Splats', '-', false, [
            'Splat count of the currently-loaded segment',
            'real splats only (padding excluded)',
            'changes per segment swap during playback'
        ].join('\n'));
        rowParent = this.dom;

        // Bottom action, separated from the readouts above (user request
        // 2026-09-09): everything else in this panel reports, this one
        // acts - and it throws away up to gigabytes and reloads the page,
        // so it sits last, away from the controls people touch often.
        // Keyboard equivalent: Ctrl+Alt+X (main.ts).
        const actionRow = document.createElement('div');
        actionRow.className = 'ftgs-stats-row ftgs-stats-action-row';
        const clearButton = document.createElement('button');
        clearButton.type = 'button';
        clearButton.className = 'ftgs-stats-action';
        clearButton.textContent = 'Clear Cache & Reload';
        clearButton.dataset.tooltip = [
            'Discard the local copies of the loaded data',
            'Segment cache (downloaded .ply) + OPFS bin decodes',
            'Forces a full re-download and re-decode on reload',
            'Same as Ctrl+Alt+X'
        ].join('\n');
        clearButton.addEventListener('click', () => this.onClearCache?.());
        actionRow.appendChild(clearButton);
        this.dom.appendChild(actionRow);
    }

    /** set once per dataset load (main.ts) - not part of the per-frame update() */
    setVariant(sourcePath: string | undefined) {
        // Spaced separators (user request 2026-09-09): at 11px a bare
        // `a/b/c.ply` reads as one long token, and the slashes disappear into
        // the descenders around them. The spaces also give the line real wrap
        // opportunities, so it breaks between path segments instead of
        // mid-filename.
        this.variantValue.textContent = sourcePath ? sourcePath.replace(/\//g, ' / ') : '-';
    }

    /**
     * Nest a control block (main.ts's source badges / stereo sliders)
     * directly BELOW the Variant row rather than at the panel's very top,
     * so the 'what is loaded' line stays first.
     */
    insertControlsBelowVariant(el: HTMLElement) {
        const variantRow = this.variantValue.parentElement;
        this.dom.insertBefore(el, variantRow ? variantRow.nextSibling : this.dom.firstChild);
    }

    get visible(): boolean {
        return !this.hidden;
    }

    toggle() {
        this.hidden = !this.hidden;
    }

    /**
     * Refresh the displayed values - cheap enough to call every frame, but
     * only does real work while visible. The timings arrive already
     * frame-to-frame-smoothed (see main.ts's EMA) - even so, re-painting
     * that DOM text every single frame (up to 200+ times/sec on a
     * high-refresh display) read as flickery/twitchy rather than stable
     * numbers, so the timing rows are only actually repainted once per
     * TIMING_RENDER_INTERVAL_MS; camera/RAM/resolution still refresh every
     * call since they're comfortably readable at full rate.
     */
    update(timings: FrameTimings, cameraPosition: { x: number; y: number; z: number }, canvas: HTMLCanvasElement, splatCount?: number, cameraTarget?: { x: number; y: number; z: number }) {
        if (this.hidden) {
            return;
        }

        const now = performance.now();
        if (now - this.lastTimingRenderTime >= this.TIMING_RENDER_INTERVAL_MS) {
            this.lastTimingRenderTime = now;
            this.metrics.summary.textContent = timings.fps > 0 ? `${timings.fps.toFixed(0)} fps` : NO_SAMPLE;
            this.frameValue.textContent = `${timings.frameMs.toFixed(MS_DECIMALS)} ms (${timings.fps.toFixed(0)} fps)`;
            this.cpuValue.textContent = formatMs(timings.cpuMs);
            this.updateValue.textContent = formatMs(timings.updateMs);
            this.submitValue.textContent = formatMs(timings.submitMs);
            this.gpuValue.textContent = formatMs(timings.gpuMs);
            this.sortValue.textContent = formatMs(timings.sortMs);
            this.drawValue.textContent = formatMs(timings.drawMs);
        }
        // three decimals (was two): these are typed back into dataset.json,
        // and 2 decimals left up to 5 mm of drift on a restored view
        const xyz = (p: { x: number; y: number; z: number }) => `${p.x.toFixed(3)}, ${p.y.toFixed(3)}, ${p.z.toFixed(3)}`;
        this.cameraValue.textContent = xyz(cameraPosition);
        this.targetValue.textContent = cameraTarget ? xyz(cameraTarget) : NOT_AVAILABLE;

        const mem = getMemoryInfo();
        this.ramValue.textContent = mem ? `${(mem.usedJSHeapSize / 1048576).toFixed(1)} MB` : NOT_AVAILABLE;

        this.resolutionValue.textContent = `${canvas.width}x${canvas.height}`;
        // optional - the FTGS viewer's caller has a live splat count to
        // pass; a plain SuperSplat scene with no FTGS node doesn't
        this.splatsValue.textContent = splatCount === undefined ? NOT_AVAILABLE : splatCount.toLocaleString();
    }
}

export { FtgsStatsOverlay, FrameTimings };
